/**
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║                    MusiKonzept Abo-Konfiguration v2                      ║
 * ╠══════════════════════════════════════════════════════════════════════════╣
 * ║                                                                          ║
 * ║  Zwei-Stufen-Modell: Basis (kostenlos) + Mitgliedschaft (39€/mtl)        ║
 * ║  Mit 2-Wochen-für-1€-Testphase                                            ║
 * ║                                                                          ║
 * ║  Verwendet: STRIPE_SECRET_KEY (ein konsolidierter Account)               ║
 * ║                                                                          ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 */

export type SubscriptionTier = 'start' | 'pro' | 'fachkraft';
export type BillingInterval = 'monthly' | 'yearly';

// Trial period in days
export const TRIAL_PERIOD_DAYS = 7;

// Trial price in EUR
export const TRIAL_PRICE_EUR = 1;

// ─────────────────────────────────────────────────────────────
// Musik-Kita-Paket (Self-Service, 590 €/Jahr)
// Neu angelegtes Stripe-Produkt (Phase 0). Nicht Teil des
// klassischen Abo-Tier-Modells (start/pro/fachkraft).
// ─────────────────────────────────────────────────────────────
export const MUSIK_KITA_PAKET_PRODUCT_ID = 'prod_Ur5hOtWBmiNibG';
export const MUSIK_KITA_PAKET_PRICE_ID = 'price_1TrNW8ErIiWtbKLAVtZLT35M';
export const MUSIK_KITA_PAKET_PRICE_EUR = 590;

// ─────────────────────────────────────────────────────────────
// Musik-Kita Zertifizierungs-Typen (zentraler Typ)
// Wird schrittweise in Komponenten/Edge-Functions verwendet.
// ─────────────────────────────────────────────────────────────
export type MusikKitaCertificationType = 'full' | 'functional' | 'team_only' | 'paket';
export type MusikKitaSiegelStatus = 'offen' | 'beantragt' | 'aktiv';


export interface TierConfig {
  id: SubscriptionTier;
  name: string;
  description: string;
  pricing: {
    monthly: {
      price: number;
      priceId: string;
      productId: string;
    };
    yearly: {
      price: number;
      priceId: string;
      productId: string;
    };
  };
  features: string[];
  mikoFeatures: {
    requests: number | 'unlimited';
    description: string;
  };
  highlighted?: boolean;
  badge?: string;
  trialDays?: number;
}

// Feature comparison data for the comparison table
export interface ComparisonFeature {
  name: string;
  category: string;
  start: string | boolean;
  pro: string | boolean;
}

export const COMPARISON_FEATURES: ComparisonFeature[] = [
  // KI-Assistent
  { name: 'Miko KI-Anfragen', category: 'KI-Assistent', start: '5/Monat', pro: 'Unbegrenzt' },
  
  // Online-Kurse
  { name: 'Online-Kurse', category: 'Lerninhalte', start: 'Starter-Bundle', pro: 'Alle Kurse' },
  { name: 'Kreativpakete', category: 'Lerninhalte', start: '1× komplett', pro: 'Alle Module' },
  { name: 'Journalartikel', category: 'Lerninhalte', start: '1 Artikel', pro: 'Alle Artikel' },
  
  // Live & Community
  { name: 'Liederwerkstatt Live', category: 'Live & Community', start: false, pro: 'Unbegrenzt' },
  { name: 'Liederwerkstatt Aufzeichnungen', category: 'Live & Community', start: false, pro: true },
  { name: 'Live-Webinare', category: 'Live & Community', start: false, pro: true },
  { name: 'Webinar-Aufzeichnungen', category: 'Live & Community', start: false, pro: true },
  { name: 'Community-Zugang', category: 'Live & Community', start: false, pro: true },
  { name: 'Monatliches Live-Onboarding', category: 'Live & Community', start: false, pro: true },
  
  // Extras
  { name: 'PDF-Downloads', category: 'Extras', start: false, pro: true },
  { name: 'Zertifikate', category: 'Extras', start: false, pro: 'Alle Kurse' },
  { name: 'Priority Support', category: 'Extras', start: false, pro: true },
  { name: 'Bis zu 3 Personen', category: 'Extras', start: false, pro: 'Du + 2 weitere Personen' },
];

export const SUBSCRIPTION_TIERS: Record<SubscriptionTier, TierConfig> = {
  start: {
    id: 'start',
    name: 'Basis',
    description: 'Kostenloser Einstieg in die MusiKonzept-Welt.',
    pricing: {
      monthly: {
        price: 0,
        priceId: '', // Kein Stripe-Preis für kostenlos
        productId: '',
      },
      yearly: {
        price: 0,
        priceId: '',
        productId: '',
      },
    },
    features: [
      'Starter-Bundle (1 Kurs + 1 Kreativpaket)',
      '1 Journalartikel',
      '5 Miko KI-Anfragen pro Monat',
      'Newsletter mit Tipps & Inspiration',
    ],
    mikoFeatures: {
      requests: 5,
      description: '5 Miko-Anfragen pro Monat',
    },
  },
  pro: {
    id: 'pro',
    name: 'Mitgliedschaft',
    description: 'Voller Zugang zu allen Inhalten und der Community.',
    pricing: {
      monthly: {
        price: 39,
        priceId: 'price_1Sg6FfErIiWtbKLAmuCB3PcU',
        productId: 'prod_TdMzSwzVfka5FM',
      },
      yearly: {
        price: 390,
        priceId: 'price_1Sg6FsErIiWtbKLA6Wwsd5Ue',
        productId: 'prod_TdMzSwzVfka5FM',
      },
    },
    features: [
      'Alle Online-Kurse komplett',
      'Alle Kreativpakete (alle Module)',
      'Alle Journalartikel',
      'Liederwerkstatt: Alle Live-Termine + Aufzeichnungen',
      'Live-Webinare + Aufzeichnungen',
      'Unbegrenzte Miko-Anfragen',
      'Community-Zugang',
      'Zertifikate für alle Kurse',
      'Monatliches Live-Onboarding',
      'Priority Support',
    'Teilbar mit bis zu 2 weiteren Personen',
    ],
    mikoFeatures: {
      requests: 'unlimited',
      description: 'Unbegrenzte Miko-Anfragen',
    },
    highlighted: true,
    badge: '2 Wochen für 1 € testen',
    trialDays: TRIAL_PERIOD_DAYS,
  },
  fachkraft: {
    id: 'fachkraft',
    name: 'Fachkraft-Sonderpreis',
    description: 'Exklusiver Sonderpreis für Absolvent:innen der Fachkraft-Ausbildung.',
    pricing: {
      monthly: {
        price: 35,
        priceId: 'price_1SyGgoErIiWtbKLAIejgdcTj',
        productId: 'prod_TdMzSwzVfka5FM', // Same product as pro (full access)
      },
      yearly: {
        price: 350,
        priceId: 'price_1SyGh6ErIiWtbKLAICCFEgs0',
        productId: 'prod_TdMzSwzVfka5FM', // Same product as pro (full access)
      },
    },
    features: [
      'Alle Online-Kurse komplett',
      'Alle Kreativpakete (alle Module)',
      'Alle Journalartikel',
      'Liederwerkstatt: Alle Live-Termine + Aufzeichnungen',
      'Live-Webinare + Aufzeichnungen',
      'Unbegrenzte Miko-Anfragen',
      'Community-Zugang',
      'Zertifikate für alle Kurse',
      'Monatliches Live-Onboarding',
      'Priority Support',
      'Teilbar mit bis zu 2 weiteren Personen',
    ],
    mikoFeatures: {
      requests: 'unlimited',
      description: 'Unbegrenzte Miko-Anfragen',
    },
    highlighted: true,
    badge: 'Sonderpreis für Absolvent:innen',
    trialDays: 0, // Kein Trial für Fachkraft (bereits volles Zugang während Ausbildung)
  },
};

// Helper to get tier by product ID
export function getTierByProductId(productId: string): SubscriptionTier | null {
  for (const [tierId, tier] of Object.entries(SUBSCRIPTION_TIERS)) {
    if (
      tier.pricing.monthly.productId === productId ||
      tier.pricing.yearly.productId === productId
    ) {
      return tierId as SubscriptionTier;
    }
  }
  return null;
}

// Helper to get tier by price ID
export function getTierByPriceId(priceId: string): SubscriptionTier | null {
  for (const [tierId, tier] of Object.entries(SUBSCRIPTION_TIERS)) {
    if (
      tier.pricing.monthly.priceId === priceId ||
      tier.pricing.yearly.priceId === priceId
    ) {
      return tierId as SubscriptionTier;
    }
  }
  return null;
}

// All product IDs for tier detection
export const ALL_PRODUCT_IDS = {
  pro: [
    SUBSCRIPTION_TIERS.pro.pricing.monthly.productId,
    SUBSCRIPTION_TIERS.pro.pricing.yearly.productId,
  ],
};

export type KreativpaketAccess = 'single' | 'full';

// Feature flags helper - simplified for two-tier model
export function getTierFeatures(tier: SubscriptionTier): {
  canAccessAllCourses: boolean;
  canAccessRotatingCourse: boolean;
  canDownloadMaterials: boolean;
  canAccessLiveWebinars: boolean;
  canAccessWebinarRecordings: boolean;
  canAccessLiederwerkstattLive: boolean;
  canAccessLiederwerkstattRecordings: boolean;
  canAccessCommunity: boolean;
  canGetCertificates: boolean;
  canGetAllCertificates: boolean;
  hasPrioritySupport: boolean;
  mikoRequestsPerMonth: number | 'unlimited';
  hasUnlimitedMiko: boolean;
  kreativpaketAccess: KreativpaketAccess;
} {
  const config = SUBSCRIPTION_TIERS[tier];
  // fachkraft gets same access as pro
  const hasPremiumAccess = tier === 'pro' || tier === 'fachkraft';
  const kreativpaketAccess: KreativpaketAccess = hasPremiumAccess ? 'full' : 'single';
  
  return {
    canAccessAllCourses: hasPremiumAccess,
    canAccessRotatingCourse: hasPremiumAccess,
    canDownloadMaterials: hasPremiumAccess,
    canAccessLiveWebinars: hasPremiumAccess,
    canAccessWebinarRecordings: hasPremiumAccess,
    canAccessLiederwerkstattLive: hasPremiumAccess,
    canAccessLiederwerkstattRecordings: hasPremiumAccess,
    canAccessCommunity: hasPremiumAccess,
    canGetCertificates: hasPremiumAccess,
    canGetAllCertificates: hasPremiumAccess,
    hasPrioritySupport: hasPremiumAccess,
    mikoRequestsPerMonth: config.mikoFeatures.requests,
    hasUnlimitedMiko: config.mikoFeatures.requests === 'unlimited',
    kreativpaketAccess,
  };
}

// Legacy tier mapping - maps old 'plus' tier to 'pro' for backwards compatibility
// Note: 'fachkraft' is intentionally NOT normalized to 'pro' as it's a separate tier
export function normalizeTier(tier: string | null | undefined): SubscriptionTier {
  const t = (tier || '').toLowerCase();
  if (t === 'pro') return 'pro';
  if (t === 'fachkraft') return 'fachkraft';
  // Map legacy 'plus' and 'basic' to 'pro' for migration
  if (t === 'plus' || t === 'basic') return 'pro';
  return 'start';
}
